import { Button, Input } from '@affine/component';
import { Modal } from '@affine/component/ui/modal';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import * as styles from './teamspace-name-dialog.css';

export interface TeamspaceNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => Promise<void>;
  onSubmitError: (error: unknown) => void;
  initialName?: string;
  title: ReactNode;
  namePlaceholder: string;
  saveLabel: ReactNode;
  cancelLabel: ReactNode;
  savingLabel: ReactNode;
  inputTestId: string;
  saveTestId: string;
}

export const TeamspaceNameDialog = ({
  open,
  onOpenChange,
  onSubmit,
  onSubmitError,
  initialName = '',
  title,
  namePlaceholder,
  saveLabel,
  cancelLabel,
  savingLabel,
  inputTestId,
  saveTestId,
}: TeamspaceNameDialogProps) => {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const trimmedName = name.trim();

  useEffect(() => {
    if (open) {
      setName(initialName);
      setSaving(false);
    }
  }, [initialName, open]);

  const close = useCallback(() => {
    if (!saving) onOpenChange(false);
  }, [onOpenChange, saving]);

  const submit = useCallback(async () => {
    if (!trimmedName || saving) return;
    setSaving(true);
    try {
      await onSubmit(trimmedName);
      onOpenChange(false);
    } catch (error) {
      onSubmitError(error);
      setSaving(false);
    }
  }, [onOpenChange, onSubmit, onSubmitError, saving, trimmedName]);

  return (
    <Modal
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) close();
      }}
      title={title}
      width={420}
    >
      <div className={styles.body}>
        <Input
          autoFocus
          autoSelect
          data-testid={inputTestId}
          placeholder={namePlaceholder}
          value={name}
          onChange={setName}
          onKeyDown={event => {
            if (event.key === 'Enter') void submit();
          }}
        />
        <div className={styles.footer}>
          <Button onClick={close}>{cancelLabel}</Button>
          <Button
            data-testid={saveTestId}
            disabled={!trimmedName || saving}
            onClick={() => void submit()}
            variant="primary"
          >
            {saving ? savingLabel : saveLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
